import { CONTRIBUTOR_SESSION_STORAGE_KEY } from "@/constants";
import axios from "axios";

type AuthToken = {
	accessToken: string;
	refreshToken: string;
};

export const setToken = (accessToken: string) => {
	const person = sessionStorage.getItem(CONTRIBUTOR_SESSION_STORAGE_KEY);
	if (!person) return;
	const updatedPerson = {
		...JSON.parse(person),
		accessToken: accessToken,
	};
	sessionStorage.setItem(
		CONTRIBUTOR_SESSION_STORAGE_KEY,
		JSON.stringify(updatedPerson)
	);
};

export const getTokens = (): AuthToken | null => {
	const stored = sessionStorage.getItem(CONTRIBUTOR_SESSION_STORAGE_KEY);
	if (!stored) return null;
	return {
		accessToken: JSON.parse(stored).accessToken,
		refreshToken: JSON.parse(stored).refreshToken,
	};
};

const server_url = process.env.REACT_APP_SERVER_DOMAIN;

const api = axios.create({
	baseURL: server_url,
});

// Flag to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

api.interceptors.request.use((config) => {
	const tokens = getTokens();
	if (tokens?.accessToken) {
		config.headers.Authorization = `Bearer ${tokens.accessToken}`;
	}
	return config;
});

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const tokens = getTokens();

		if (
			error.response?.status === 403 &&
			tokens?.refreshToken &&
			!originalRequest._retry
		) {
			if (isRefreshing) {
				return new Promise(function (resolve, reject) {
					failedQueue.push({ resolve, reject });
				})
					.then((token) => {
						originalRequest.headers["Authorization"] = "Bearer " + token;
						return api(originalRequest);
					})
					.catch((err) => Promise.reject(err));
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				const res = await axios.post(`${server_url}auth/tokenrefresh`, {
					refresh: tokens.refreshToken,
				});

				setToken(res.data.access);
				api.defaults.headers.common["Authorization"] = "Bearer " + res.data.access;
				processQueue(null, res.data.access);

				return api(originalRequest);
			} catch (err) {
				processQueue(err, null);
				// Optional: clear tokens and redirect to login
				sessionStorage.removeItem(CONTRIBUTOR_SESSION_STORAGE_KEY);
				return Promise.reject(err);
			} finally {
				isRefreshing = false;
			}
		}

		return Promise.reject(error);
	}
);

export default api;
